/**
 * The tow line the game is named after.
 *
 * Physically it is a one-sided spring: a rope pulls when stretched past its
 * paid-out length and does nothing at all when slack. A damping term along the
 * rope axis stops the tug-and-tow pair from bouncing like a bungee, and a
 * tension ceiling parts the line if the player tries to snatch a 1,400-tonne
 * barge up to speed.
 *
 * Visually it is a tube swept along a hanging curve. The sag comes from the
 * slack fraction, so the rope visibly droops to the water when you ease off and
 * straightens bar-taut as load comes on — which is also your main tension
 * gauge when you are not watching the HUD.
 */

import {
  BufferGeometry,
  CatmullRomCurve3,
  Color,
  DoubleSide,
  Mesh,
  MeshBasicNodeMaterial,
  TubeGeometry,
  Vector3,
} from 'three/webgpu'
import type { Vessel } from '../physics/Vessel.ts'
import { sampleWaterHeight } from '../world/waves.ts'

/** Newtons per metre of stretch. Nylon hawsers are stretchy; that is a feature. */
const STIFFNESS = 60_000
/** Damping along the rope axis, N per m/s of separation rate. */
const DAMPING = 45_000
/** Above this tension the line parts. */
export const BREAKING_TENSION = 2_200_000

/*
 * Arcade towing. Newton says the rope pulls both ends equally; Newton has
 * never watched a five-year-old lose a berthing because the tow yanked the
 * stern around. Three cheats, all confined to this file:
 */
/** The barge feels the whole tension, the tug only this share of it. */
const TUG_SHARE = 0.35
/**
 * Trailer assist: yaw torque steering the towed hull to point up the rope,
 * so it trails behind like a wagon instead of slewing on its own ideas.
 * Proportional and damping gains, N·m per rad and per rad/s.
 */
const TRAILER_ALIGN = 1.3e8
const TRAILER_DAMP = 2.2e8
/** Assist fades in with tension so a slack rope doesn't steer anybody. */
const TRAILER_FULL_AT = 90_000

const SEGMENTS = 32
// Thick enough not to alias to a hairline, and rendered flat (unlit) so a
// grazing view can't split the tube into a bright top edge and a dark under-
// edge — which read as two parallel ropes.
const ROPE_RADIUS = 0.16

const _a = new Vector3()
const _b = new Vector3()
const _axis = new Vector3()
const _relVel = new Vector3()
const _force = new Vector3()
const _bodyPoint = new Vector3()
const _sag = new Vector3()

export interface TowEndpoint {
  vessel: Vessel
  /** Attachment point in the vessel's body frame. */
  bodyPoint: Vector3
}

export class Hawser {
  readonly mesh: Mesh

  /** Current tension in newtons. Zero when slack or unhooked. */
  tension = 0
  /** Paid-out rope length in metres. */
  length = 40

  private from: TowEndpoint | null = null
  private to: TowEndpoint | null = null
  private readonly material: MeshBasicNodeMaterial
  private readonly curvePoints: Vector3[] = []

  constructor() {
    // Unlit: a uniformly-lit tube is always one clean line, whatever the angle.
    // Double-sided so that when the rope is taut and you sight nearly down its
    // length, the tube's far wall fills the middle instead of leaving only its
    // two silhouette edges — which read as two ropes.
    this.material = new MeshBasicNodeMaterial({ color: new Color('#cdbf98'), side: DoubleSide })
    this.mesh = new Mesh(new BufferGeometry(), this.material)
    this.mesh.frustumCulled = false
    this.mesh.visible = false
    this.mesh.name = 'hawser'

    for (let i = 0; i <= SEGMENTS; i++) this.curvePoints.push(new Vector3())
  }

  get connected(): boolean {
    return this.from !== null && this.to !== null
  }

  /** World position of the towed body, or null when nothing is on the wire. */
  get toPosition(): Vector3 | null {
    return this.to ? this.to.vessel.position : null
  }

  attach(from: TowEndpoint, to: TowEndpoint): void {
    this.from = from
    this.to = to
    // Pay out a little more rope than the current gap so the hook-up itself
    // doesn't jerk either vessel.
    from.vessel.localToWorld(from.bodyPoint, _a)
    to.vessel.localToWorld(to.bodyPoint, _b)
    this.length = Math.max(_a.distanceTo(_b) * 1.15, 45)
    this.mesh.visible = true
  }

  release(): void {
    this.from = null
    this.to = null
    this.tension = 0
    this.mesh.visible = false
  }

  /**
   * Apply the rope force to both vessels. Returns true if the line parted this
   * step (the caller decides what that means for the contract).
   */
  step(): boolean {
    if (!this.from || !this.to) return false

    this.from.vessel.localToWorld(this.from.bodyPoint, _a)
    this.to.vessel.localToWorld(this.to.bodyPoint, _b)

    _axis.copy(_b).sub(_a)
    const separation = _axis.length()

    if (separation <= this.length || separation < 1e-3) {
      this.tension = 0
      return false
    }

    _axis.divideScalar(separation)

    const stretch = separation - this.length
    _relVel.copy(this.to.vessel.velocity).sub(this.from.vessel.velocity)
    const separationRate = _relVel.dot(_axis)

    // Full signed damping, floored at zero: a closing tow slackens the rope
    // (down to nothing — a rope cannot push), which is what stops the barge
    // surging past the tug on its own spring energy.
    this.tension = Math.max(STIFFNESS * stretch + DAMPING * separationRate, 0)

    if (this.tension > BREAKING_TENSION) {
      this.release()
      return true
    }

    // Each end applied at its own bollard so the rope can turn a hull as well
    // as drag it — but not equal and opposite: the tug takes only TUG_SHARE,
    // so the tow never wrestles the helm away from the player.
    _force.copy(_axis).multiplyScalar(this.tension * TUG_SHARE)
    _force.y = 0
    _bodyPoint.copy(this.from.bodyPoint)
    this.from.vessel.applyForceAtPoint(_force, _bodyPoint)

    _force.copy(_axis).multiplyScalar(-this.tension)
    _force.y = 0
    _bodyPoint.copy(this.to.bodyPoint)
    this.to.vessel.applyForceAtPoint(_force, _bodyPoint)

    // Trailer assist: steer the tow's bow up the rope. The axis points from
    // tug to barge, so the barge should head along -axis.
    const barge = this.to.vessel
    const desiredYaw = Math.atan2(-_axis.x, -_axis.z)
    let error = desiredYaw - barge.yaw
    error = Math.atan2(Math.sin(error), Math.cos(error))
    const grip = Math.min(this.tension / TRAILER_FULL_AT, 1)
    barge.applyYawTorque((TRAILER_ALIGN * error - TRAILER_DAMP * barge.yawRate) * grip)

    return false
  }

  /** Rebuild the rope tube. Call once per rendered frame while connected. */
  updateVisual(elapsed: number): void {
    if (!this.from || !this.to) return

    this.from.vessel.localToWorld(this.from.bodyPoint, _a)
    this.to.vessel.localToWorld(this.to.bodyPoint, _b)

    const separation = _a.distanceTo(_b)
    // How much rope is spare, as a fraction — drives the droop depth.
    const slack = Math.max(0, 1 - separation / this.length)
    const droop = slack * this.length * 0.45

    for (let i = 0; i <= SEGMENTS; i++) {
      const t = i / SEGMENTS
      const point = this.curvePoints[i]
      point.copy(_a).lerp(_b, t)

      // Parabolic sag, deepest mid-span. A slack rope floats on the sea rather
      // than passing through it.
      const sagDepth = droop * 4 * t * (1 - t)
      _sag.set(0, -sagDepth, 0)
      point.add(_sag)
      if (droop > 0.5) {
        const water = sampleWaterHeight(point.x, point.z, elapsed)
        if (point.y < water + 0.05) point.y = water + 0.05
      }
    }

    const curve = new CatmullRomCurve3(this.curvePoints)
    this.mesh.geometry.dispose()
    this.mesh.geometry = new TubeGeometry(curve, SEGMENTS, ROPE_RADIUS, 5, false)

    // Tension read-out in the rope itself: pale manila when easy, angry when
    // approaching the breaking strain.
    const strain = Math.min(this.tension / BREAKING_TENSION, 1)
    this.material.color.setRGB(0.84 + strain * 0.12, 0.79 - strain * 0.45, 0.64 - strain * 0.5)
  }
}
